import React, { useState, useEffect } from 'react';
import {
  Phone,
  MapPin,
  Mail,
  Clock,
  Edit2,
  Save,
  X,
  Trash2,
  AlertTriangle,
  ShoppingCart,
  Sprout,
  Users,
  CreditCard,
  RefreshCw,
  CheckCircle,
  ExternalLink,
  Building2,
  Globe
} from 'lucide-react';
import { collection, getDocs, writeBatch, doc } from 'firebase/firestore';
import { db } from '../../firebase/config';
import { useContact, ContactInfo } from '../../contexts/ContactContext';

interface ConfirmDialogProps {
  isOpen: boolean;
  title: string;
  message: string;
  recordType: string;
  recordCount: number;
  onConfirm: () => void;
  onCancel: () => void;
  isLoading: boolean;
}

const ConfirmDialog: React.FC<ConfirmDialogProps> = ({
  isOpen,
  title,
  message,
  recordType,
  recordCount,
  onConfirm,
  onCancel,
  isLoading
}) => {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-2xl max-w-md w-full overflow-hidden">
        <div className="bg-gradient-to-r from-red-500 to-red-600 p-6">
          <div className="flex items-center gap-4">
            <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
              <AlertTriangle className="h-6 w-6 text-white" />
            </div>
            <div>
              <h3 className="text-xl font-bold text-white">{title}</h3>
              <p className="text-red-100 text-sm">This action cannot be undone</p>
            </div>
          </div>
        </div>
        <div className="p-6">
          <p className="text-gray-600 dark:text-gray-300 mb-4">{message}</p>
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-xl p-4 mb-6">
            <div className="flex items-center justify-between">
              <span className="text-red-700 dark:text-red-300 font-medium">{recordType}</span>
              <span className="text-red-800 dark:text-red-200 font-bold">{recordCount} records</span>
            </div>
          </div>
          <div className="flex gap-3">
            <button
              onClick={onCancel}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gray-100 dark:bg-gray-700 text-gray-700 dark:text-gray-300 font-semibold rounded-xl hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              onClick={onConfirm}
              disabled={isLoading}
              className="flex-1 px-4 py-3 bg-gradient-to-r from-red-500 to-red-600 text-white font-semibold rounded-xl hover:from-red-600 hover:to-red-700 transition-all disabled:opacity-50 flex items-center justify-center gap-2"
            >
              {isLoading ? (
                <>
                  <RefreshCw className="h-5 w-5 animate-spin" />
                  Deleting...
                </>
              ) : (
                <>
                  <Trash2 className="h-5 w-5" />
                  Delete All
                </>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Admin: React.FC = () => {
  const { contactInfo, updateContactInfo, isLoading: isLoadingContact, isSaving } = useContact();
  const [isEditingContact, setIsEditingContact] = useState(false);
  const [editForm, setEditForm] = useState<ContactInfo>(contactInfo);

  // Record counts
  const [recordCounts, setRecordCounts] = useState({
    orders: 0,
    plants: 0,
    customers: 0,
    payments: 0
  });
  const [loadingCounts, setLoadingCounts] = useState(true);

  // Deletion state
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: '',
    message: '',
    recordType: '',
    recordCount: 0,
    collectionName: '' as string | 'all'
  });
  const [isDeleting, setIsDeleting] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    setEditForm(contactInfo);
  }, [contactInfo]);

  useEffect(() => {
    fetchRecordCounts();
  }, []);

  const fetchRecordCounts = async () => {
    setLoadingCounts(true);
    try {
      const [ordersSnap, plantsSnap, customersSnap, paymentsSnap] = await Promise.all([
        getDocs(collection(db, 'orders')),
        getDocs(collection(db, 'plants')),
        getDocs(collection(db, 'customers')),
        getDocs(collection(db, 'payments'))
      ]);

      setRecordCounts({
        orders: ordersSnap.size,
        plants: plantsSnap.size,
        customers: customersSnap.size,
        payments: paymentsSnap.size
      });
    } catch (error) {
      console.error('Error fetching record counts:', error);
    } finally {
      setLoadingCounts(false);
    }
  };

  const handleEditContact = () => {
    setEditForm(contactInfo);
    setIsEditingContact(true);
    setErrorMessage('');
  };

  const handleSaveContact = async () => {
    try {
      setErrorMessage('');
      await updateContactInfo(editForm);
      setIsEditingContact(false);
      setSuccessMessage('Contact information saved to database successfully!');
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error saving contact info:', error);
      setErrorMessage('Failed to save contact information. Please try again.');
      setTimeout(() => setErrorMessage(''), 5000);
    }
  };

  const handleCancelEdit = () => {
    setEditForm(contactInfo);
    setIsEditingContact(false);
    setErrorMessage('');
  };

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      if (confirmDialog.collectionName === 'all') {
        await deleteCollection('orders');
        await deleteCollection('plants');
        await deleteCollection('customers');
        await deleteCollection('payments');
        setSuccessMessage('All records have been deleted successfully!');
      } else {
        await deleteCollection(confirmDialog.collectionName);
        setSuccessMessage(`All ${confirmDialog.recordType.toLowerCase()} have been deleted successfully!`);
      }

      await fetchRecordCounts();
      setConfirmDialog({ ...confirmDialog, isOpen: false });
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      console.error('Error deleting records:', error);
      alert('Failed to delete records. Please try again.');
    } finally {
      setIsDeleting(false);
    }
  };

  const deleteCollection = async (collectionName: string) => {
    const querySnapshot = await getDocs(collection(db, collectionName));
    const batch = writeBatch(db);

    querySnapshot.docs.forEach((document) => {
      batch.delete(doc(db, collectionName, document.id));
    });

    await batch.commit();
  };

  return (
    <div className="space-y-6 min-h-screen transition-colors duration-200">
      {/* Success Message */}
      {successMessage && (
        <div className="bg-green-50 dark:bg-green-900/30 border border-green-200 dark:border-green-800 rounded-2xl p-4 shadow-lg animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-green-100 dark:bg-green-800 rounded-xl flex items-center justify-center">
              <CheckCircle className="h-5 w-5 text-green-600 dark:text-green-400" />
            </div>
            <p className="text-green-700 dark:text-green-300 font-medium">{successMessage}</p>
          </div>
        </div>
      )}

      {/* Error Message */}
      {errorMessage && (
        <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-2xl p-4 shadow-lg animate-fade-in">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-red-100 dark:bg-red-800 rounded-xl flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-red-600 dark:text-red-400" />
            </div>
            <p className="text-red-700 dark:text-red-300 font-medium">{errorMessage}</p>
          </div>
        </div>
      )}

      {/* Contact Information Section */}
      <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-xl border border-gray-100 dark:border-gray-700 overflow-hidden">
        {/* Header */}
        <div className="bg-gradient-to-r from-emerald-500 via-teal-500 to-cyan-500 p-6">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-2xl flex items-center justify-center">
                <Building2 className="h-7 w-7 text-white" />
              </div>
              <div>
                <h2 className="text-2xl font-bold text-white">Contact Information</h2>
                <p className="text-emerald-100 text-sm">Manage your business contact details</p>
              </div>
            </div>
            {!isEditingContact ? (
              <button
                onClick={handleEditContact}
                className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all font-medium backdrop-blur-sm border border-white/20"
              >
                <Edit2 className="h-4 w-4" />
                Edit Details
              </button>
            ) : (
              <div className="flex gap-3">
                <button
                  onClick={handleSaveContact}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white text-emerald-600 rounded-xl transition-all font-semibold hover:bg-emerald-50 shadow-lg disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  {isSaving ? (
                    <>
                      <RefreshCw className="h-4 w-4 animate-spin" />
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="h-4 w-4" />
                      Save Changes
                    </>
                  )}
                </button>
                <button
                  onClick={handleCancelEdit}
                  disabled={isSaving}
                  className="flex items-center gap-2 px-5 py-2.5 bg-white/20 hover:bg-white/30 text-white rounded-xl transition-all font-medium backdrop-blur-sm border border-white/20 disabled:opacity-70 disabled:cursor-not-allowed"
                >
                  <X className="h-4 w-4" />
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>

        <div className="p-6">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
            {/* Primary Phone */}
            <div className="group">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-emerald-100 dark:bg-emerald-900/50 rounded-lg flex items-center justify-center">
                  <Phone className="h-4 w-4 text-emerald-600 dark:text-emerald-400" />
                </div>
                Primary Phone Number
              </label>
              {isEditingContact ? (
                <input
                  type="tel"
                  value={editForm.primaryPhone}
                  onChange={(e) => setEditForm({ ...editForm, primaryPhone: e.target.value })}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-emerald-500 focus:ring-4 focus:ring-emerald-500/20 transition-all text-lg"
                  placeholder="+91 XXXXXXXXXX"
                />
              ) : (
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 group-hover:border-emerald-300 dark:group-hover:border-emerald-700 transition-colors">
                  <p className="text-gray-900 dark:text-white font-semibold text-lg">{contactInfo.primaryPhone}</p>
                </div>
              )}
            </div>

            {/* Secondary Phone */}
            <div className="group">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-blue-100 dark:bg-blue-900/50 rounded-lg flex items-center justify-center">
                  <Phone className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                </div>
                Secondary Phone Number
              </label>
              {isEditingContact ? (
                <input
                  type="tel"
                  value={editForm.secondaryPhone}
                  onChange={(e) => setEditForm({ ...editForm, secondaryPhone: e.target.value })}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-blue-500 focus:ring-4 focus:ring-blue-500/20 transition-all text-lg"
                  placeholder="+91 XXXXXXXXXX"
                />
              ) : (
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 group-hover:border-blue-300 dark:group-hover:border-blue-700 transition-colors">
                  <p className="text-gray-900 dark:text-white font-semibold text-lg">{contactInfo.secondaryPhone}</p>
                </div>
              )}
            </div>

            {/* Email */}
            <div className="group">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-purple-100 dark:bg-purple-900/50 rounded-lg flex items-center justify-center">
                  <Mail className="h-4 w-4 text-purple-600 dark:text-purple-400" />
                </div>
                Email Address
              </label>
              {isEditingContact ? (
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({ ...editForm, email: e.target.value })}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-purple-500 focus:ring-4 focus:ring-purple-500/20 transition-all text-lg"
                  placeholder="email@example.com"
                />
              ) : (
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 group-hover:border-purple-300 dark:group-hover:border-purple-700 transition-colors">
                  <p className="text-gray-900 dark:text-white font-semibold text-lg">{contactInfo.email}</p>
                </div>
              )}
            </div>

            {/* Google Maps Link */}
            <div className="group">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-rose-100 dark:bg-rose-900/50 rounded-lg flex items-center justify-center">
                  <Globe className="h-4 w-4 text-rose-600 dark:text-rose-400" />
                </div>
                Google Maps Link
              </label>
              {isEditingContact ? (
                <input
                  type="url"
                  value={editForm.mapsLink}
                  onChange={(e) => setEditForm({ ...editForm, mapsLink: e.target.value })}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-rose-500 focus:ring-4 focus:ring-rose-500/20 transition-all"
                  placeholder="https://maps.google.com/..."
                />
              ) : (
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 group-hover:border-rose-300 dark:group-hover:border-rose-700 transition-colors">
                  <a
                    href={contactInfo.mapsLink}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-rose-600 dark:text-rose-400 hover:text-rose-700 dark:hover:text-rose-300 font-semibold inline-flex items-center gap-2 transition-colors"
                  >
                    View on Google Maps
                    <ExternalLink className="h-4 w-4" />
                  </a>
                </div>
              )}
            </div>

            {/* Address - Full Width */}
            <div className="lg:col-span-2 group">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-amber-100 dark:bg-amber-900/50 rounded-lg flex items-center justify-center">
                  <MapPin className="h-4 w-4 text-amber-600 dark:text-amber-400" />
                </div>
                Business Address
              </label>
              {isEditingContact ? (
                <textarea
                  value={editForm.address}
                  onChange={(e) => setEditForm({ ...editForm, address: e.target.value })}
                  rows={2}
                  className="w-full px-4 py-3.5 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-amber-500 focus:ring-4 focus:ring-amber-500/20 transition-all resize-none text-lg"
                  placeholder="Enter complete business address"
                />
              ) : (
                <div className="px-4 py-3.5 bg-gradient-to-r from-gray-50 to-gray-100 dark:from-gray-700/50 dark:to-gray-700 rounded-xl border border-gray-200 dark:border-gray-600 group-hover:border-amber-300 dark:group-hover:border-amber-700 transition-colors">
                  <p className="text-gray-900 dark:text-white font-semibold text-lg">{contactInfo.address}</p>
                </div>
              )}
            </div>

            {/* Business Hours - Full Width */}
            <div className="lg:col-span-2">
              <label className="flex items-center gap-2 text-sm font-semibold text-gray-700 dark:text-gray-300 mb-2">
                <div className="w-8 h-8 bg-indigo-100 dark:bg-indigo-900/50 rounded-lg flex items-center justify-center">
                  <Clock className="h-4 w-4 text-indigo-600 dark:text-indigo-400" />
                </div>
                Business Hours
              </label>
              {isEditingContact ? (
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Weekdays</label>
                    <input
                      type="text"
                      placeholder="Monday - Saturday: 8:00 AM - 6:00 PM"
                      value={editForm.businessHours.weekdays}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        businessHours: { ...editForm.businessHours, weekdays: e.target.value }
                      })}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Weekends</label>
                    <input
                      type="text"
                      placeholder="Sunday: 9:00 AM - 2:00 PM"
                      value={editForm.businessHours.weekends}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        businessHours: { ...editForm.businessHours, weekends: e.target.value }
                      })}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all"
                    />
                  </div>
                  <div>
                    <label className="text-xs text-gray-500 dark:text-gray-400 mb-1 block">Note</label>
                    <input
                      type="text"
                      placeholder="Closed on major holidays"
                      value={editForm.businessHours.note}
                      onChange={(e) => setEditForm({
                        ...editForm,
                        businessHours: { ...editForm.businessHours, note: e.target.value }
                      })}
                      className="w-full px-4 py-3 border-2 border-gray-200 dark:border-gray-600 rounded-xl bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:border-indigo-500 focus:ring-4 focus:ring-indigo-500/20 transition-all"
                    />
                  </div>
                </div>
              ) : (
                <div className="bg-gradient-to-r from-indigo-50 to-purple-50 dark:from-indigo-900/20 dark:to-purple-900/20 rounded-xl border border-indigo-200 dark:border-indigo-800 p-4">
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                      <span className="text-gray-900 dark:text-white font-medium">{contactInfo.businessHours.weekdays}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-amber-500 rounded-full"></div>
                      <span className="text-gray-900 dark:text-white font-medium">{contactInfo.businessHours.weekends}</span>
                    </div>
                    <div className="flex items-center gap-3">
                      <div className="w-2 h-2 bg-red-500 rounded-full"></div>
                      <span className="text-gray-600 dark:text-gray-400 italic">{contactInfo.businessHours.note}</span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Quick Stats Cards */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        {[
          { name: 'Orders', count: recordCounts.orders, icon: ShoppingCart, gradient: 'from-blue-500 to-blue-600', bg: 'bg-blue-50 dark:bg-blue-900/20' },
          { name: 'Plants', count: recordCounts.plants, icon: Sprout, gradient: 'from-green-500 to-emerald-600', bg: 'bg-green-50 dark:bg-green-900/20' },
          { name: 'Farmers', count: recordCounts.customers, icon: Users, gradient: 'from-purple-500 to-purple-600', bg: 'bg-purple-50 dark:bg-purple-900/20' },
          { name: 'Payments', count: recordCounts.payments, icon: CreditCard, gradient: 'from-amber-500 to-orange-600', bg: 'bg-amber-50 dark:bg-amber-900/20' }
        ].map((stat) => (
          <div
            key={stat.name}
            className={`${stat.bg} rounded-2xl p-5 border border-gray-200 dark:border-gray-700 hover:shadow-lg transition-all`}
          >
            <div className="flex items-center justify-between mb-3">
              <div className={`w-12 h-12 bg-gradient-to-br ${stat.gradient} rounded-xl flex items-center justify-center shadow-lg`}>
                <stat.icon className="h-6 w-6 text-white" />
              </div>
              <span className="text-3xl font-bold text-gray-900 dark:text-white">
                {loadingCounts ? '...' : stat.count}
              </span>
            </div>
            <h3 className="text-sm font-semibold text-gray-600 dark:text-gray-400">{stat.name}</h3>
          </div>
        ))}
      </div>

      {/* Confirmation Dialog */}
      <ConfirmDialog
        isOpen={confirmDialog.isOpen}
        title={confirmDialog.title}
        message={confirmDialog.message}
        recordType={confirmDialog.recordType}
        recordCount={confirmDialog.recordCount}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDialog({ ...confirmDialog, isOpen: false })}
        isLoading={isDeleting}
      />
    </div>
  );
};

export default Admin;
